import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
export function useOnboarding() { return useContext(Ctx); }

// Route → checklist keys that get auto-ticked when the user visits them.
const ROUTE_MARK = {
  "/":                    ["visit_dashboard"],
  "/mar-chart":           ["visit_mar_chart"],
  "/cd-ledger":           ["visit_cd_ledger"],
  "/rota":                ["visit_rota"],
  "/staff-schedule":      ["visit_staff_schedule"],
  "/care-plans":          ["visit_care_plans"],
  "/users":               ["visit_users"],
  "/audit":               ["visit_audit"],
  "/family-portal":       ["visit_family_portal"],
};

function matchRoute(pathname) {
  if (ROUTE_MARK[pathname]) return ROUTE_MARK[pathname];
  if (pathname.startsWith("/clients/")) return ["visit_client_profile"];
  return null;
}

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);            // Help Centre modal
  const [tourActive, setTourActive] = useState(false); // Guided tour visible

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/onboarding/status");
      setStatus(r.data);
    } catch { /* noop */ }
  }, [user]);

  const mark = useCallback(async (key) => {
    if (!user || !key) return;
    // Skip if already completed (avoid needless API calls)
    if (status?.items?.find((i) => i.key === key)?.completed) return;
    try {
      const r = await api.post("/onboarding/mark", { key });
      setStatus(r.data);
    } catch { /* noop */ }
  }, [user, status]);

  const reset = useCallback(async () => {
    try { const r = await api.post("/onboarding/reset"); setStatus(r.data); } catch { /* noop */ }
  }, []);

  const dismiss = useCallback(async (v = true) => {
    try { await api.post("/onboarding/dismiss", { dismissed: v }); await refresh(); } catch { /* noop */ }
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-mark route-based checks
  useEffect(() => {
    const keys = matchRoute(location.pathname);
    if (!keys) return;
    keys.forEach((k) => mark(k));
  }, [location.pathname, mark]);

  return (
    <Ctx.Provider value={{ status, refresh, mark, reset, dismiss, open, setOpen, tourActive, setTourActive }}>
      {children}
    </Ctx.Provider>
  );
}
